import React, { useEffect, useState, useCallback } from 'react';
import accessLocalStorage from '../../../services/accessLocalStorage';
import axios from 'axios';
import './PjsipTuning.css';

const EDITABLE_FIELDS = [
  { key: 'threadpool_initial_size',   label: 'threadpool_initial_size',   help: 'Tamanho inicial do pool (1–100)',           min: 1,   max: 100 },
  { key: 'threadpool_auto_increment', label: 'threadpool_auto_increment', help: 'Incremento ao saturar (1–50)',              min: 1,   max: 50 },
  { key: 'threadpool_idle_timeout',   label: 'threadpool_idle_timeout',   help: 'Timeout idle em segundos (30–3600)',        min: 30,  max: 3600 },
  { key: 'threadpool_max_size',       label: 'threadpool_max_size',       help: 'Máximo do pool (10–500)',                   min: 10,  max: 500 },
  { key: 'timer_t1',                  label: 'timer_t1 (ms)',             help: 'SIP T1 — retransmissões (100–10000)',       min: 100, max: 10000 },
  { key: 'timer_b',                   label: 'timer_b (ms)',              help: 'SIP B — fim de transação (>= 64 × T1)',     min: 6400, max: 128000 },
];

async function apiGet() {
  const { token, ipRequest } = accessLocalStorage.getUserLocalStorage();
  const r = await axios.get(`${ipRequest}api/config/pjsip-tuning/`, {
    headers: { Authorization: token },
  });
  // ETag vem no header — backend já envia.
  const etag = r.headers.etag || r.data.etag;
  return { ...r.data, etag };
}

async function apiPost(updates, etag) {
  const { token, ipRequest } = accessLocalStorage.getUserLocalStorage();
  const r = await axios.post(`${ipRequest}api/config/pjsip-tuning/`, { updates }, {
    headers: { Authorization: token, 'If-Match': etag },
  });
  return r.data;
}

function PjsipTuning() {
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(null);
  const [form, setForm] = useState({});
  const [etag, setEtag] = useState(null);
  const [busy, setBusy] = useState(false);
  const [alert, setAlert] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGet();
      setCurrent(data);
      setForm(data.values || {});
      setEtag(data.etag);
      setAlert(null);
    } catch (err) {
      const msg = err.response?.data?.error || 'Erro ao carregar';
      setAlert({ type: 'error', msg });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setField = (k, v) => {
    const num = parseInt(v, 10);
    setForm((s) => ({ ...s, [k]: Number.isFinite(num) ? num : '' }));
  };

  const handleSave = async () => {
    const updates = {};
    for (const f of EDITABLE_FIELDS) {
      const cur = current.values?.[f.key];
      const nv = form[f.key];
      if (cur !== nv && nv !== '' && nv !== undefined && nv !== null) {
        updates[f.key] = nv;
      }
    }
    if (Object.keys(updates).length === 0) {
      setAlert({ type: 'info', msg: 'Nenhuma mudança a aplicar.' });
      return;
    }
    if (!window.confirm(
      `Aplicar ${Object.keys(updates).length} mudança(s) em [system] do pjsip.conf?\n\n`
      + `IMPORTANTE: tunings em [system] (threadpool/timers) só entram em vigor após\n`
      + `\`systemctl restart asterisk\` em janela de manutenção — reload NÃO basta.\n\n`
      + `O backup do arquivo atual será criado automaticamente.`
    )) {
      return;
    }
    setBusy(true);
    try {
      const r = await apiPost(updates, etag);
      setAlert({ type: 'success', msg: `Salvo. ${r.warning || ''}` });
      await load();
    } catch (err) {
      const code = err.response?.data?.code;
      const msg = err.response?.data?.error || 'Erro ao salvar';
      if (code === 'CONFIG_DRIFTED') {
        setAlert({ type: 'error', msg: 'pjsip.conf foi modificado externamente. Recarregando…' });
        await load();
      } else {
        setAlert({ type: 'error', msg });
      }
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="pjt-loading">Carregando…</div>;

  return (
    <div className="pjt-container">
      <header className="pjt-header">
        <h1>Tunings PJSIP — seção [system]</h1>
        {current?.asteriskVersion && (
          <div className="pjt-version">{current.asteriskVersion}</div>
        )}
      </header>

      <div className="pjt-warning">
        ⚠️ <strong>Mudanças em <code>[system]</code> só entram em vigor após
        <code> systemctl restart asterisk</code></strong> (derruba chamadas).
        Planeje aplicação em janela de manutenção. <code>pjsip reload</code> NÃO
        recarrega threadpool/timers.
      </div>

      {alert && (
        <div className={`pjt-alert pjt-alert-${alert.type}`}>{alert.msg}</div>
      )}

      <div className="pjt-grid">
        {EDITABLE_FIELDS.map((f) => (
          <div key={f.key} className="pjt-field">
            <label htmlFor={`pjt-${f.key}`}>{f.label}</label>
            <input
              id={`pjt-${f.key}`}
              type="number"
              min={f.min}
              max={f.max}
              value={form[f.key] ?? ''}
              onChange={(e) => setField(f.key, e.target.value)}
              disabled={busy}
            />
            <div className="pjt-help">
              {f.help}
              {current?.values?.[f.key] !== undefined && form[f.key] !== current.values[f.key] && (
                <span className="pjt-changed"> · atual: {current.values[f.key]}</span>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="pjt-actions">
        <button type="button" className="pjt-btn pjt-btn-secondary" onClick={load} disabled={busy}>
          Recarregar
        </button>
        <button type="button" className="pjt-btn pjt-btn-primary" onClick={handleSave} disabled={busy}>
          {busy ? 'Salvando…' : 'Salvar (apenas no arquivo)'}
        </button>
      </div>

      <div className="pjt-footer">
        ETag atual: <code>{etag}</code>
        {current?.sectionFound === false && (
          <span className="pjt-warning-inline"> · ⚠️ Seção [system] não encontrada no pjsip.conf</span>
        )}
      </div>
    </div>
  );
}

export default PjsipTuning;
